/**
 * Metadata refresh logic for FilterController
 * Handles loading filters from IndexedDB filter_metadata and updating UI
 */

import { setupOutsideClickHandler } from './filter-controller.ui.js';

// debugMode is global, defined in params.js

/**
 * Create options array for filter group from metadata
 * @param {string} group - Filter group (L, F, M, S)
 * @param {Array} uniqueValues - Unique values for the group (should be sorted)
 * @param {Object} filterMetaData - Full filter metadata including mgGroups
 * @param {Object} ctx - FilterController context (optional, for sorting)
 * @returns {Array} Options array for Treeselect
 */
export function createOptionsForGroup(group, uniqueValues, filterMetaData, ctx = null) {
    if (group === 'M' && filterMetaData && filterMetaData.mgGroups && Object.keys(filterMetaData.mgGroups).length > 0) {
        // For M group, create hierarchical structure: MG -> M[] for grouped items, flat for ungrouped
        if (debugMode === '1') {
            console.log('[FilterController] Creating hierarchical M options', {
                mgGroupsKeys: Object.keys(filterMetaData.mgGroups),
                mgGroupsCount: Object.keys(filterMetaData.mgGroups).length,
                mgGroups: filterMetaData.mgGroups
            });
        }
        const opts = [];
        
        // Collect all M values that belong to MG groups
        const mValuesInGroups = new Set();
        for (const mValues of Object.values(filterMetaData.mgGroups)) {
            if (Array.isArray(mValues)) {
                mValues.forEach(m => mValuesInGroups.add(m));
            }
        }
        
        // Process MG groups and create hierarchical structure
        for (const [mg, mValues] of Object.entries(filterMetaData.mgGroups)) {
            if (!Array.isArray(mValues) || mValues.length === 0) {
                if (debugMode === '1') {
                    console.warn('[FilterController] Invalid mgGroups entry:', { mg, mValues });
                }
                continue;
            }
            
            // For 'empty' MG group, add M values as flat items
            if (mg === 'empty') {
                // Sort by filterOrder['M'] if available
                let sortedEmptyValues = mValues.filter(mVal => mVal !== 'empty');
                if (ctx && ctx.filterOrder && ctx.filterOrder['M'] && Array.isArray(ctx.filterOrder['M'])) {
                    const orderMap = new Map();
                    ctx.filterOrder['M'].forEach((val, idx) => {
                        orderMap.set(val, idx);
                    });
                    sortedEmptyValues.sort((a, b) => {
                        const orderA = orderMap.has(a) ? orderMap.get(a) : 9999;
                        const orderB = orderMap.has(b) ? orderMap.get(b) : 9999;
                        return orderA - orderB;
                    });
                }
                
                const emptyGroupValues = sortedEmptyValues
                    .map(mVal => {
                        let mTranslation = mVal;
                        try {
                            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                                mTranslation = window.TranslationRegistry.translate(mVal, window.LANG) || mVal;
                            }
                        } catch (e) {
                            mTranslation = mVal;
                        }
                        return {
                            name: mTranslation,
                            value: mVal,
                            children: []
                        };
                    });
                opts.push(...emptyGroupValues);
                continue;
            }
            
            // For non-empty MG groups, create hierarchical structure
            let mgTranslation = mg;
            try {
                if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                    mgTranslation = window.TranslationRegistry.translate(mg, window.LANG) || mg;
                }
            } catch (e) {
                mgTranslation = mg;
            }
            
            const children = mValues
                .filter(mVal => mVal !== 'empty')
                .map(mVal => {
                    let mTranslation = mVal;
                    try {
                        if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                            mTranslation = window.TranslationRegistry.translate(mVal, window.LANG) || mVal;
                        }
                    } catch (e) {
                        mTranslation = mVal;
                    }
                    return {
                        name: mTranslation,
                        value: mVal,
                        children: []
                    };
                });
            
            // Sort children by filterOrder['M'] if available
            if (ctx && ctx.filterOrder && ctx.filterOrder['M'] && Array.isArray(ctx.filterOrder['M'])) {
                const orderMap = new Map();
                ctx.filterOrder['M'].forEach((val, idx) => {
                    orderMap.set(val, idx);
                });
                children.sort((a, b) => {
                    const orderA = orderMap.has(a.value) ? orderMap.get(a.value) : 9999;
                    const orderB = orderMap.has(b.value) ? orderMap.get(b.value) : 9999;
                    return orderA - orderB;
                });
            }
            
            if (children.length === 0) continue;
            
            opts.push({
                name: mgTranslation,
                value: mg,
                isGroupSelectable: false,
                children: children
            });
        }
        
        // Add M values that are NOT in any MG group as flat items
        // Use sorted order from ctx.filters['M'] if available
        const sortedMValues = ctx && ctx.filters && ctx.filters['M'] ? ctx.filters['M'] : uniqueValues.M;
        for (const mVal of sortedMValues) {
            if (mVal === 'empty') continue;
            if (!mValuesInGroups.has(mVal)) {
                let mTranslation = mVal;
                try {
                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                        mTranslation = window.TranslationRegistry.translate(mVal, window.LANG) || mVal;
                    }
                } catch (e) {
                    mTranslation = mVal;
                }
                opts.push({
                    name: mTranslation,
                    value: mVal,
                    children: []
                });
            }
        }
        
        // Sort options according to filterOrder['M'] if available
        if (ctx && ctx.filterOrder && ctx.filterOrder['M'] && Array.isArray(ctx.filterOrder['M'])) {
            const orderMap = new Map();
            ctx.filterOrder['M'].forEach((val, idx) => {
                orderMap.set(val, idx);
            });
            
            // Sort function: get order index for option value
            const getOrderIndex = (opt) => {
                if (opt.children && opt.children.length > 0) {
                    // For MG groups, use first child's order
                    const firstChild = opt.children[0];
                    return orderMap.has(firstChild.value) ? orderMap.get(firstChild.value) : 9999;
                }
                return orderMap.has(opt.value) ? orderMap.get(opt.value) : 9999;
            };
            
            opts.sort((a, b) => {
                const orderA = getOrderIndex(a);
                const orderB = getOrderIndex(b);
                return orderA - orderB;
            });
            
            // Also sort children within each MG group
            opts.forEach(opt => {
                if (opt.children && opt.children.length > 0) {
                    opt.children.sort((a, b) => {
                        const orderA = orderMap.has(a.value) ? orderMap.get(a.value) : 9999;
                        const orderB = orderMap.has(b.value) ? orderMap.get(b.value) : 9999;
                        return orderA - orderB;
                    });
                }
            });
        }
        
        return opts;
    } else {
        // For other groups, create flat structure, skip 'empty' values
        return uniqueValues[group]
            .filter(val => val !== 'empty')
            .map(val => {
                let translation = val;
                try {
                    if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
                        translation = window.TranslationRegistry.translate(val, window.LANG) || val;
                    }
                } catch (e) {
                    translation = val;
                }
                return {
                    name: translation,
                    value: val,
                    children: []
                };
            });
    }
}

/**
 * Apply URL categories to filter trees
 * @param {Object} ctx - FilterController context
 * @param {Object} config - Configuration with startCategories
 */
export function applyUrlCategories(ctx, config) {
    if (config.startCategories && Object.keys(config.startCategories).some(k => config.startCategories[k].length > 0)) {
        setTimeout(() => {
            for (const [group, values] of Object.entries(config.startCategories)) {
                if (values && values.length > 0 && ctx.trees[group]) {
                    if (typeof ctx.trees[group].updateValue === 'function') {
                        ctx.trees[group].updateValue(values);
                    }
                    ctx.handleFilterChange(group, values);
                }
            }
            ctx.updateFilterCounter();
        }, 200);
    }
}

/**
 * Initialize UI from metadata (first time initialization)
 * @param {Object} ctx - FilterController context
 * @param {Object} filterMeta - Filter metadata
 */
export function initializeUIFromMetadata(ctx, filterMeta) {
    // Get translations from TranslationRegistry if not provided
    let translationsObj = ctx.translations || {};
    if (!translationsObj || Object.keys(translationsObj).length === 0) {
        if (window.TranslationRegistry && typeof window.TranslationRegistry.getAllTranslations === 'function') {
            translationsObj = window.TranslationRegistry.getAllTranslations() || {};
        }
    }
    
    const config = {
        translations: translationsObj,
        language: ctx.language || window.LANG || 'EN',
        startCategories: {}
    };
    
    // Extract URL categories before initializing UI
    const urlCategories = $.urlParam('cat');
    if (urlCategories) {
        ctx.extractUrlParameters(config);
    }
    
    ctx.initializeUI(config);
    ctx.updateFilterCounter();
    setupOutsideClickHandler(ctx);
    
    // Mark URL categories as applied if they were extracted
    if (urlCategories && config.startCategories && Object.keys(config.startCategories).some(k => config.startCategories[k].length > 0)) {
        ctx._urlCategoriesApplied = true;
        applyUrlCategories(ctx, config);
    }
}

/**
 * Update tree for a specific group with new options
 * @param {Object} ctx - FilterController context
 * @param {string} group - Filter group
 * @param {Object} tree - Existing tree instance
 * @param {Array} opts - New options array
 */
export function updateTreeForGroup(ctx, group, tree, opts) {
    // Save current selection before destroying
    const savedSelection = tree?.allValue || [];
    
    // Destroy existing tree
    if (tree && typeof tree.destroy === 'function') {
        tree.destroy();
    }
    
    // Recreate Treeselect with new options
    const container = document.querySelector(`.filter-${group}`);
    if (container) {
        ctx.trees[group] = new Treeselect({
            direction: 'bottom',
            clearable: false,
            searchable: false,
            parentHtmlContainer: container,
            value: savedSelection,
            placeholder: (ctx.translations && ctx.translations[group] && ctx.translations[group][ctx.language]) ? ctx.translations[group][ctx.language] : group,
            options: opts,
            closeOnSelect: false,
            multiple: true,
            nameChangeCallback: function(sel) {},
            inputCallback: (sel) => {
                ctx.handleFilterChange(group, sel);
                let filters_counter = 0;
                $.each(ctx.trees, (key, value) => { filters_counter += ctx.trees[key].allValue.length; });
                $('.fnd-filter-counter').text(filters_counter > 0 ? filters_counter : "");
                // Note: applyFilters is already called in handleFilterChange, no need to call it again here
            },
            openCallback: () => {
                // Activate hover mode when dropdown opens via click
                if (!ctx.hoverModeActive) {
                    ctx.hoverModeActive = true;
                }
                
                // Close all other open dropdowns when this one opens
                for (const [otherKey, otherTree] of Object.entries(ctx.trees)) {
                    if (otherKey !== group && otherTree && otherTree.srcElement) {
                        const inputElement = otherTree.srcElement.querySelector('.treeselect-input');
                        if (inputElement && inputElement.classList.contains('treeselect-input--opened')) {
                            if (typeof otherTree.toggleOpenClose === 'function') {
                                otherTree.toggleOpenClose();
                            }
                        }
                    }
                }
            },
            closeCallback: () => {
                // Deactivate hover mode if all dropdowns are closed
                let anyOpen = false;
                for (const [otherKey, otherTree] of Object.entries(ctx.trees)) {
                    if (otherTree && otherTree.srcElement) {
                        const inputElement = otherTree.srcElement.querySelector('.treeselect-input');
                        if (inputElement && inputElement.classList.contains('treeselect-input--opened')) {
                            anyOpen = true;
                            break;
                        }
                    }
                }
                if (!anyOpen) {
                    ctx.hoverModeActive = false;
                }
            }
        });
        ctx.trees[group].mount();
        
        // Set data-label for background text only if missing
        setTimeout(() => {
            const treeselectInput = container.querySelector('.treeselect-input');
            if (treeselectInput && !treeselectInput.getAttribute('data-label')) {
                const label = (ctx.translations && ctx.translations[group] && ctx.translations[group][ctx.language]) 
                    ? ctx.translations[group][ctx.language] 
                    : group;
                treeselectInput.setAttribute('data-label', label.toUpperCase());
            }
        }, 100);
    }
}

/**
 * Remove tree and container for a group
 * @param {Object} ctx - FilterController context
 * @param {string} group - Filter group
 * @param {Object} tree - Tree instance to destroy
 */
export function removeTreeForGroup(ctx, group, tree) {
    if (tree && typeof tree.destroy === 'function') {
        tree.destroy();
        delete ctx.trees[group];
    }
    const container = document.querySelector(`.filter-${group}`);
    if (container) {
        container.remove();
    }
}

/**
 * Restore filters from activeFilters state
 * @param {Object} ctx - FilterController context
 */
export function restoreFiltersFromState(ctx) {
    // Restore category filters
    for (const [group, values] of Object.entries(ctx.activeFilters.categories)) {
        if (Array.isArray(values) && values.length > 0 && ctx.trees[group]) {
            if (typeof ctx.trees[group].updateValue === 'function') {
                ctx.trees[group].updateValue(values);
            }
        }
    }
    
    // Setup outside click handler
    setupOutsideClickHandler(ctx);
    
    // Restore date filters to UI inputs
    const hasDateFilters = !!(ctx.activeFilters.dateFrom || ctx.activeFilters.dateTo);
    if (hasDateFilters) {
        const $dateFrom = window.jQuery ? window.jQuery('.filter-date-from') : null;
        const $dateTo = window.jQuery ? window.jQuery('.filter-date-to') : null;
        
        if ($dateFrom && $dateFrom.length && ctx.activeFilters.dateFrom) {
            $dateFrom.val(ctx.activeFilters.dateFrom);
        }
        if ($dateTo && $dateTo.length && ctx.activeFilters.dateTo) {
            $dateTo.val(ctx.activeFilters.dateTo);
        }
        
        // Sync LiteDateRangePicker with restored dates
        if (window.__drp && typeof window.__drp.applyRange === 'function') {
            const fromDate = ctx.activeFilters.dateFrom ? new Date(ctx.activeFilters.dateFrom + 'T00:00:00') : null;
            const toDate = ctx.activeFilters.dateTo ? new Date(ctx.activeFilters.dateTo + 'T00:00:00') : null;
            window.__drp.applyRange(fromDate, toDate, 'restore');
        }
    }
    
    // Update availability after delay
    setTimeout(() => {
        if (typeof ctx.refreshAvailability === 'function') {
            ctx.refreshAvailability();
        }
    }, 500);
}

/**
 * Refresh filters from metadata
 * Main function that orchestrates filter refresh from IndexedDB metadata
 * @param {Object} ctx - FilterController context
 * @param {Object} filterMeta - Filter metadata from IndexedDB
 */
export function refreshFiltersFromMetadata(ctx, filterMeta) {
    
    try {
        if (!filterMeta || !filterMeta.uniqueValues) {
            if (debugMode === '1') {
                console.warn('[FilterController] Invalid filter metadata', filterMeta);
            }
            if (window.HyperLog) {
                window.HyperLog.warn('[FilterController] Invalid filter metadata');
            }
            return;
        }
        
        // Prevent double call
        if (ctx._isRefreshing) {
            return;
        }
        ctx._isRefreshing = true;
        
        const { uniqueValues } = filterMeta;
        
        // Update filters object with unique values from metadata
        ctx.filters = {
            L: uniqueValues.L || [],
            F: uniqueValues.F || [],
            M: uniqueValues.M || [],
            S: uniqueValues.S || [],
            mgGroups: filterMeta.mgGroups || {}
        };
        
        // Sort filters according to predefined order
        ctx.sortFilters(ctx.language || window.LANG || 'EN');
        
        
        // Initialize UI if trees don't exist
        const hasTrees = ctx.trees && Object.keys(ctx.trees).length > 0;
        if (!hasTrees) {
            initializeUIFromMetadata(ctx, filterMeta);
            ctx._isRefreshing = false;
            return;
        }
        
        // Apply URL categories if not applied yet
        const urlCategories = $.urlParam('cat');
        if (urlCategories && !ctx._urlCategoriesApplied) {
            const config = { startCategories: {}, filters: ctx.filters };
            ctx.extractUrlParameters(config);
            ctx._urlCategoriesApplied = true;
            applyUrlCategories(ctx, config);
        }
        
        // Reload UI with new filter values
        if (ctx.trees && Object.keys(ctx.trees).length > 0) {
            for (const [group, tree] of Object.entries(ctx.trees)) {
                if (!uniqueValues[group] || uniqueValues[group].length === 0) {
                    removeTreeForGroup(ctx, group, tree);
                    continue;
                }
                
                // Check if all values are 'empty'
                const validValues = uniqueValues[group].filter(v => v !== 'empty');
                if (validValues.length === 0) {
                    removeTreeForGroup(ctx, group, tree);
                    continue;
                }
                
                // Create options for group
                // Use sorted filters from ctx.filters instead of uniqueValues to preserve sort order
                const sortedUniqueValues = {
                    ...uniqueValues,
                    [group]: ctx.filters[group] || uniqueValues[group] || []
                };
                const opts = createOptionsForGroup(group, sortedUniqueValues, filterMeta, ctx);
                
                // Skip if no valid options
                if (opts.length === 0) {
                    removeTreeForGroup(ctx, group, tree);
                    continue;
                }
                
                // Update tree with new options
                updateTreeForGroup(ctx, group, tree, opts);
            }
            
            // Restore filters from state
            restoreFiltersFromState(ctx);
            
            // Reset flag after async operations
            setTimeout(() => {
                ctx._isRefreshing = false;
            }, 1000);
        } else {
            ctx._isRefreshing = false;
        }
    } catch (error) {
        if (window.HyperLog) {
            window.HyperLog.error('[FilterController] Error refreshing filters from metadata', { error: error?.message });
        }
        ctx._isRefreshing = false;
    }
}

